import type { ComparisonMessages } from "./comparable";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Button } from "@/components/design/Button";
import { BoxHeader } from "@/components/design/BoxHeader";
import { userErrorMessage } from "@/lib/user-error";

export interface ComparisonSection<T> {
  key: string;
  title: ReactNode;
  render: (item: T) => ReactNode;
  boxed?: boolean;
}
export function ComparisonPage<T>({ kind, messages, allowed, valid, loading, error, retry, items, name, identity, sections, searchPath }: {
  kind: string; messages: ComparisonMessages; allowed: boolean; valid: boolean; loading: boolean; error?: Error; retry: () => void;
  items: readonly [T | null | undefined, T | null | undefined]; name: (item: T) => string; identity: (item: T) => ReactNode;
  sections: ComparisonSection<T>[]; searchPath: string;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  return <div id={`${kind}-comparison-page`} className="glass-section mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10">
    <header className="mb-6 flex flex-wrap items-center gap-4"><BackButton /><h1 className="text-smycles-title font-medium"><FormattedMessage id={messages.title} /></h1></header>
    {!allowed ? <p><FormattedMessage id={messages.denied} /></p> : !valid ? <div className="space-y-4"><p id={`${kind}-comparison-empty`}><FormattedMessage id={messages.empty} /></p><Button id={`${kind}-comparison-empty-search`} onClick={() => navigate(searchPath)}><FormattedMessage id={messages.search} /></Button></div>
      : loading ? <p role="status"><FormattedMessage id={messages.loading} /></p>
      : error ? <div role="alert" className="space-y-4"><p>{userErrorMessage(intl, error)}</p><Button onClick={retry}><FormattedMessage id={messages.retry} /></Button></div>
      : <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
          {items.map((item, index) => <div key={index} id={`${kind}-comparison-${index === 0 ? "left" : "right"}`} className="box min-w-0 bg-surface p-5">
            {item ? identity(item) : <p><FormattedMessage id={messages.unavailable} /></p>}
          </div>)}
        </div>
        {sections.map((section) => <section key={section.key} className="grid grid-cols-1 items-start gap-4 @md:grid-cols-2">
          {items.map((item, index) => <div key={index} id={`${kind}-comparison-${section.key}-${index === 0 ? "left" : "right"}`} className={section.boxed !== false || !item ? "box min-w-0 break-words bg-surface p-5" : "min-w-0"}>
            {(section.boxed !== false || !item) && <BoxHeader title={section.title} />}
            <p className="mb-4 text-smycles-label text-ink-muted @md:hidden">{item ? name(item) : intl.formatMessage({ id: messages.unavailable })}</p>
            {item ? section.render(item) : <p className="text-smycles-meta text-ink-muted"><FormattedMessage id={messages.unavailable} /></p>}
          </div>)}
        </section>)}
      </div>}
  </div>;
}
